import { NavigateFunction, useNavigate } from "react-router";
import MeritoDarkLogo from "../../assets/images/MeritoDarkLogo.webp";
import WALogo from "../../assets/images/ipLogos/whatsapp.svg";
import styles from "./sfpl-landing.module.scss";
import MeritoButton from "../../components/common/MeritoButtons/MeritoButtons";
import { MdOutlineVerifiedUser } from "react-icons/md";
import { FaArrowRight } from "react-icons/fa6";
import { Field, Form } from "react-final-form";
import OutlinedFormInput from "../Finance/components/OutlinedFormInput/OutlinedFormInput";
import {
  digitsOnly,
  onBlurFormatNumber,
  parseNumberForDB,
  removeSpace,
} from "../../components/functions/utilities";
import MeritoSelectField from "../Underwriting/components/selectfields/MeritoSelectField";
import { RestS } from "../../rest/rest-service";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { FORM_ERROR } from "final-form";
import { CommonError } from "../../interfaces/interfaces";
import CalendlyPopup from "../DecisionHub/Components/calendlyPopup/CalendlyPopup";
import { RiCheckboxCircleFill } from "react-icons/ri";
import {
  countryCodeValidation,
  mobileNumberValidation,
  mobileNumberValidationDis,
} from "../../components/functions/formValidation";
import {
  allowOnlyNumber,
  allowOnlyNumberPlus,
} from "../UserManagement/components/NewUserModal";
import { useSearchParams } from "react-router-dom";

type HeaderNavProps = {
  navigator: NavigateFunction;
};

type FooterNavProps = {
  navigator: NavigateFunction;
};

type ConsultationProps = {
  setShowCalendlyPopup: Dispatch<SetStateAction<boolean>>;
};

type HeroSectionProps = {
  isLoading: boolean;
  isSuccess: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsSuccess: Dispatch<SetStateAction<boolean>>;
  setShowCalendlyPopup: Dispatch<SetStateAction<boolean>>;
  prefillValues: any;
};

type CreditLineBuilderProps = {
  isLoading: boolean;
  isSuccess: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsSuccess: Dispatch<SetStateAction<boolean>>;
  setShowCalendlyPopup: Dispatch<SetStateAction<boolean>>;
  prefillValues;
};

type ErrorMap = Record<string, string>;

enum RepaymentMode {
  QUARTERLY = "QUARTERLY",
  MONTHLY = "MONTHLY",
  ONE_SHOT = "ONE_SHOT",
}

const REPAYMENT_MODES = [
  { label: "Quarterly", value: RepaymentMode.QUARTERLY },
  { label: "Monthly", value: RepaymentMode.MONTHLY },
  { label: "One Shot", value: RepaymentMode.ONE_SHOT },
];

const SFPL_REASONS = [
  {
    title: "Cheapest",
    desc: "Rate Match Guarantee. We’ll beat any other unsecured loan rate.",
  },
  { title: "Flexible", desc: "Repay when you want, between 3-18 months." },
  { title: "Largest", desc: "Up to ₹15 Cr in 48 hours." },
];

const ADDITIONAL_ITEMS = [
  {
    title: "Revolving Credit Line",
    desc: "Like a bank OD: Pay vendors, repay in 30-90 days to reset the limit.",
  },
  {
    title: "Supply Chain Finance",
    desc: "Extend vendor credit period without taking debt on your books.",
  },
];

const ABHISHEK_MOBILE_NUMBER = "919867054499";

const HeaderNav: React.FC<HeaderNavProps> = ({ navigator }) => {
  return (
    <div id="indexHeader" className={styles.header}>
      <div
        className={styles.brandLogo}
        onClick={() => navigator("/")}
        style={{ cursor: "pointer" }}
      >
        <img
          style={{ maxWidth: "88%" }}
          src={MeritoDarkLogo}
          alt="brnadlogo"
          className="img-fluid"
          height={"100%"}
          width={"100%"}
        />
      </div>
      <div className={styles.consultationButton}>
        <MeritoButton
          type="primary-1"
          size="lg"
          isRounded={true}
          icon={<img src={WALogo} alt="WhatsApp" className="img-fluid" />}
          btnText="Live Consultation"
          onClick={() => {
            window.open(`https://wa.me/${ABHISHEK_MOBILE_NUMBER}`, "_blank");
          }}
        />
      </div>
    </div>
  );
};

const HeroSection: React.FC<HeroSectionProps> = ({
  isLoading,
  isSuccess,
  setIsLoading,
  setIsSuccess,
  setShowCalendlyPopup,
  prefillValues,
}) => {
  return (
    <div className={styles.heroContainer}>
      <div className={styles.heroTextContainer}>
        <div className={styles.heroText}>
          <span>Sell First,</span>
          <div>
            <span className={styles.highlightedText}>(Re)</span>Pay Later.
          </div>
          <span>Power Your Festive Growth.</span>
        </div>
        <div className={styles.heroSubText}>
          <p>Customised credit line for D2C brands.</p>
          <p>Fund your growth, repay when you want.</p>
        </div>
        <div className={styles.rateMatchTextContainer}>
          <MdOutlineVerifiedUser color="#21A366" />
          <p className={styles.rateMatchText}>
            Backed by a Rate Match Guarantee
          </p>
        </div>
      </div>
      <CreditLineBuilder
        setShowCalendlyPopup={setShowCalendlyPopup}
        isLoading={isLoading}
        isSuccess={isSuccess}
        setIsLoading={setIsLoading}
        setIsSuccess={setIsSuccess}
        prefillValues={prefillValues}
      />
    </div>
  );
};

const CreditLineBuilder: React.FC<CreditLineBuilderProps> = ({
  isLoading,
  isSuccess,
  setIsLoading,
  setIsSuccess,
  setShowCalendlyPopup,
  prefillValues,
}) => {
  const generateAndDownloadExcel = async (
    values: any
  ): Promise<ErrorMap | void> => {
    console.log("Generating and downloading Excel with values:", values); // Log input values

    setIsLoading(true);

    try {
      console.log("Making API call to generate Excel..."); // Log the API call start
      const fileData = await RestS.generateAndDownloadSFPLExcel({
        tenor: values.tenor,
        loanSanctionAmount: values.loanSanctionAmount,
        annualRevenueRunRate: values.annualRevenueRunRate,
        repaymentMode: values.repaymentMode.label,
        phoneNumber: `${values.phoneCode}${values.phoneNumber}`,
        brandName: values.brandName,
      });
      // Cast fileData to ArrayBuffer explicitly
      const arrayBuffer = fileData as ArrayBuffer;
      const fileBlob = new Blob([arrayBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a link to trigger the download
      const downloadLink = document.createElement("a");
      downloadLink.href = URL.createObjectURL(fileBlob);
      downloadLink.download = "Calculator-Details.xlsx"; // Set filename
      downloadLink.click(); // Trigger the download

      console.log("Excel generated and download initiated successfully"); // Log success
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      console.error("Error occurred while generating Excel:", err); // Log the error

      const errorData = err.response?.data?.errors as CommonError[] | undefined;

      if (errorData?.length) {
        console.log("Received API field-level errors:", errorData); // Log field-level errors
        // Return API field-level errors
        return errorData.reduce<ErrorMap>((acc, { field, message }) => {
          acc[field || FORM_ERROR] = message;
          return acc;
        }, {});
      }

      // Generic fallback error
      console.log("Generic error message returned"); // Log generic error fallback
      return { [FORM_ERROR]: "Something went wrong. Please try again." };
    }
  };

  return (
    <div className={styles.creditLineBuilder}>
      <div className={styles.creditLineTextSection}>
        <p className={styles.mainText}>Credit Line Builder</p>
        <p className={styles.subText}>
          Get a customised credit plan with transparent costs and a clear
          repayment schedule.
        </p>
      </div>
      <div style={{ height: "0.75rem" }}></div>
      {isSuccess ? (
        <div className={styles.successMessage}>
          <RiCheckboxCircleFill className={styles.greenSvg} />
          <h3 className={styles.successTitle}>Your Plan is Ready!</h3>
          <p className={styles.successDesc}>
            Book a free consultation to understand your best options.
          </p>
        </div>
      ) : (
        <Form
          initialValues={{
            repaymentMode: {
              label: "Quarterly",
              value: RepaymentMode.QUARTERLY,
            },
            brandName: prefillValues.brandName || "",
            phoneCode:
              prefillValues.phoneCode == "+"
                ? "+91"
                : prefillValues.phoneCode || "+91",
            phoneNumber: prefillValues.phoneNumber || "",
          }}
          onSubmit={(values) => {
            setIsSuccess(true);
            generateAndDownloadExcel(values);
            return {};
          }}
          validate={(values) => {
            let errors = {};
            if (
              values.repaymentMode.value === RepaymentMode.ONE_SHOT &&
              values.tenor > 3
            ) {
              errors["tenor"] =
                "For one shot, tenor should not be more than 3.";
            }
            const tenorValidValues = [12, 15, 18, 24];
            if (
              values.repaymentMode.value == RepaymentMode.QUARTERLY &&
              !tenorValidValues.includes(Number(values.tenor))
            ) {
              errors["tenor"] =
                "For quarterly, tenor can only be 12, 15, 18 and 24.";
            }

            // if monthly then tenor should be between 6 and 24
            if (
              values.repaymentMode.value == RepaymentMode.MONTHLY &&
              (values.tenor < 6 || values.tenor > 24)
            ) {
              errors["tenor"] =
                "For monthly, tenor should be between 6 and 24.";
            }

            if (values.tenor > 24) {
              errors["tenor"] = "Tenor cannot be more than 24 months";
            }
            if (values.loanSanctionAmount > 50_00_00_000) {
              errors["loanSanctionAmount"] = "Loan amount cannot exceed 50cr";
            }

            let mobileNumberError = null;
            if (countryCodeValidation(values?.phoneCode)) {
              errors["phoneCode"] = `${countryCodeValidation(
                values?.phoneCode
              )} phone code`;
            } else if (mobileNumberValidation(values?.phoneNumber)) {
              // console.log('phoneCode')

              mobileNumberError =
                mobileNumberValidation(values?.phoneNumber) == "Required field"
                  ? "Enter phone number"
                  : mobileNumberValidation(values?.phoneNumber);
              errors["phoneNumber"] = mobileNumberError;
            }

            console.log("mobileNumberError", mobileNumberError);
            console.log("phoneNumber", values?.phoneNumber);
            if (
              !mobileNumberError &&
              values?.phoneCode == "+91" &&
              !(!values?.phoneNumber || values?.phoneNumber?.length == 10)
            ) {
              errors["phoneNumber"] = "Please enter a valid mobile number";
            }

            return errors;
          }}
          render={({
            values,
            handleSubmit,
            pristine,
            hasValidationErrors,
            errors,
            form,
          }) => {
            console.log("errors:", errors);
            return (
              <form
                id="creditLineForm"
                onSubmit={handleSubmit}
                className={styles.formSection}
              >
                <Field
                  name="loanSanctionAmount"
                  validate={(value) => {
                    if (!value) {
                      return "Enter amount";
                    }
                  }}
                >
                  {(props) => (
                    <OutlinedFormInput
                      {...props.input}
                      label="Loan Amount"
                      showError={true}
                      inpuType="number"
                      name={props.input.name}
                      value={props.input.value}
                      onChange={(e) => {
                        const numericValue = parseNumberForDB(
                          e.target.value,
                          false
                        );
                        props.input.onChange(numericValue);
                      }}
                      error={props.meta.error}
                      touched={props.meta.touched}
                      onBlur={(e) => {
                        const value = props.input.value;
                        const formattedValue = onBlurFormatNumber(value, 0);
                        props.input.onChange(formattedValue);
                        props.input.onBlur(e);
                      }}
                      maxLength={21}
                      isCurrency={true}
                      unitPlaceHolder={"₹"}
                      unitPosition={"left"}
                      fieldType="lg"
                    />
                  )}
                </Field>

                <Field
                  name="annualRevenueRunRate"
                  validate={(value) => {
                    if (!value) {
                      return "Enter amount";
                    }
                  }}
                >
                  {(props) => (
                    <OutlinedFormInput
                      {...props.input}
                      label="ARR (Annual Revenue Rate)"
                      showError={true}
                      inpuType="number"
                      name={props.input.name}
                      value={props.input.value}
                      onChange={(e) => {
                        const numericValue = parseNumberForDB(
                          e.target.value,
                          false
                        );
                        props.input.onChange(numericValue);
                      }}
                      error={props.meta.error}
                      touched={props.meta.touched}
                      onBlur={(e) => {
                        const value = props.input.value;
                        const formattedValue = onBlurFormatNumber(value, 0);
                        props.input.onChange(formattedValue);
                        props.input.onBlur(e);
                      }}
                      maxLength={21}
                      isCurrency={true}
                      unitPlaceHolder={"₹"}
                      unitPosition={"left"}
                      fieldType="lg"
                    />
                  )}
                </Field>

                <Field
                  name="repaymentMode"
                  validate={(value) => {
                    if (!value) {
                      return "Please select a repayment mode.";
                    }
                  }}
                >
                  {(props) => (
                    <MeritoSelectField
                      {...props.input}
                      size="md"
                      error={props.meta.error}
                      touched={props.meta.touched}
                      isClearable={false}
                      options={REPAYMENT_MODES}
                      label="Repayment Mode"
                      optionLabelKey="label"
                      value={props.input.value}
                      onChange={props.input.onChange}
                    />
                  )}
                </Field>

                <Field
                  name="tenor"
                  validate={(value) => {
                    if (!value) {
                      return "Enter tenor";
                    }
                  }}
                >
                  {(props) => (
                    <OutlinedFormInput
                      {...props.input}
                      label="Tenor (in months)"
                      showError={true}
                      inpuType="number"
                      name={props.input.name}
                      value={props.input.value}
                      onChange={(e) => {
                        const numericValue = parseNumberForDB(
                          e.target.value,
                          false
                        );
                        props.input.onChange(numericValue);
                      }}
                      error={props.meta.error}
                      touched={props.meta.touched}
                      onBlur={(e) => {
                        const value = props.input.value;
                        const formattedValue = onBlurFormatNumber(value, 0);
                        props.input.onChange(formattedValue);
                        props.input.onBlur(e);
                      }}
                      maxLength={2}
                      isCurrency={true}
                      // unitPlaceHolder={"₹"}
                      unitPosition={"left"}
                      fieldType="lg"
                    />
                  )}
                </Field>

                <Field
                  name="brandName"
                  validate={(value) => {
                    if (!value) {
                      return "Enter brand name";
                    }
                  }}
                >
                  {(props) => (
                    <OutlinedFormInput
                      {...props.input}
                      label="Brand Name"
                      showError={true}
                      inpuType="text"
                      placeholder="Acme Brand"
                      name={props.input.name}
                      value={props.input.value}
                      onChange={props.input.onChange}
                      error={props.meta.error}
                      touched={props.meta.touched}
                      // unitPlaceHolder={"₹"}
                      unitPosition={"left"}
                      fieldType="lg"
                    />
                  )}
                </Field>

                <div className={styles.phoneFieldRootContainer}>
                  <div className={`${styles.lg} ${styles.label}`}>Contact</div>
                  <div className={styles.phoneFieldContainer}>
                    <div className={styles.phoneCodeField}>
                      <Field
                        name="phoneCode"
                        defaultValue="+91"
                        validate={(values) => {
                          if (values) {
                            return countryCodeValidation(values);
                          } else return "";
                        }}
                      >
                        {(props) => {
                          return (
                            <OutlinedFormInput
                              {...props.input}
                              maxLength={4}
                              fieldType="lg"
                              error={
                                (errors?.phoneCode || errors?.phoneNumber) &&
                                props.meta.touched
                                  ? errors?.phoneCode || errors?.phoneNumber
                                  : props.meta.submitError
                                  ? props.meta.submitError
                                  : null
                              }
                              showBorder={true}
                              showError={false}
                              value={props.input.value}
                              onChange={(e) => {
                                const sanitizedValue = removeSpace(
                                  e.target.value
                                );
                                const validate =
                                  allowOnlyNumberPlus(sanitizedValue);

                                if (validate) {
                                  // To handle case where they try to
                                  // enter a four digit number
                                  if (
                                    sanitizedValue &&
                                    sanitizedValue[0] !== "+" &&
                                    sanitizedValue.length > 3
                                  ) {
                                    return;
                                  }

                                  props.input.onChange(sanitizedValue);
                                }
                              }}
                              onBlur={() => {
                                // to handle case where they enter nothing and blur
                                if (!props.input.value)
                                  props.input.onChange(`+91`);
                                // To handle case where they only enter number and blur
                                else if (props.input.value[0] !== "+")
                                  props.input.onChange(
                                    `+${props.input.value.slice(0, 3)}`
                                  );
                                // To handle case where they enter only + and then blur
                                else if (
                                  props.input.value[0] === "+" &&
                                  props.input.value.length == 1
                                )
                                  props.input.onChange(`+91`);
                              }}
                              placeholder="+91"
                              touched={true}
                            />
                          );
                        }}
                      </Field>
                    </div>
                    <div style={{ flexGrow: "1" }}>
                      <Field
                        name="phoneNumber"
                        validate={(values) => {
                          if (values) {
                            return mobileNumberValidation(values);
                          } else return "";
                        }}
                      >
                        {(props) => {
                          return (
                            <OutlinedFormInput
                              {...props.input}
                              maxLength={11}
                              fieldType="lg"
                              error={
                                (errors?.phoneCode || errors?.phoneNumber) &&
                                props.meta.touched
                                  ? errors?.phoneCode || errors?.phoneNumber
                                  : props.meta.submitError
                                  ? props.meta.submitError
                                  : null
                              }
                              showBorder={true}
                              showError={false}
                              value={props.input.value}
                              onChange={(e) => {
                                const sanitizedValue = digitsOnly(
                                  e.target.value
                                );

                                allowOnlyNumber(sanitizedValue)
                                  ? props.input.onChange(sanitizedValue)
                                  : {};
                              }}
                              placeholder="9876543210"
                              touched={true}
                            />
                          );
                        }}
                      </Field>
                    </div>
                  </div>
                  {errors?.phoneCode ||
                  (errors?.phoneNumber &&
                    (form.getFieldState("phoneCode")?.touched ||
                      form.getFieldState("phoneNumber")?.touched)) ? (
                    <div className={styles.errorMessage}>
                      {errors?.phoneCode ? (
                        <div>{errors?.phoneCode}</div>
                      ) : errors?.phoneNumber ? (
                        <div>{errors?.phoneNumber}</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                <MeritoButton
                  type="secondary-2"
                  size="lg"
                  isRounded={true}
                  btnText="Download Plan"
                  extraStyles={{
                    gridColumn: "span 2",
                    justifySelf: "center",
                  }}
                  btnType="submit"
                  loading={isLoading}
                  onClick={handleSubmit}
                />
              </form>
            );
          }}
        />
      )}
      <MeritoButton
        type="primary-1"
        size="lg"
        isRounded={true}
        btnText="Book Live Consultation"
        extraStyles={{ width: "100%" }}
        onClick={() => setShowCalendlyPopup(true)}
      />
    </div>
  );
};

const ReasonSection = () => {
  return (
    <div className={styles.reasonSection}>
      <div className={styles.reasonBoxTitle}>
        Why choose{" "}
        <span className={styles.reasonTitleHighlighted}>
          Sell First, Pay Later?
        </span>
      </div>
      <div className={styles.reasonBoxSection}>
        {SFPL_REASONS.map(({ title, desc }) => {
          return (
            <div className={styles.reasonBox}>
              <h3 className={styles.reasonTitle}>{title}</h3>
              <p className={styles.reasonDesc}>{desc}</p>
            </div>
          );
        })}
      </div>
      <div className={styles.reasonBonusSection}>
        <div className={styles.reasonBonusLabel}>Bonus</div>
        <div className={styles.reasonBonusContent}>
          <div className={styles.reasonBonusContentTitle}>
            Get a Free Growth Plan
          </div>
          <div className={styles.reasonBonusContentDesc}>
            Backed by Benchmarking 650+ D2C Brands.
          </div>
        </div>
      </div>
    </div>
  );
};

const AdditionalItemsSection = () => {
  return (
    <div className={styles.reasonSection}>
      <div className={styles.reasonBoxTitle}>
        Apart from Sell First, Pay Later, Merito also does -
      </div>
      <div className={styles.reasonBoxSection}>
        {ADDITIONAL_ITEMS.map(({ title, desc }) => {
          return (
            <div className={styles.reasonBox}>
              <h3 className={styles.reasonTitle}>{title}</h3>
              <p className={styles.reasonDesc}>{desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BookConsultationSection: React.FC<ConsultationProps> = ({
  setShowCalendlyPopup,
}) => {
  return (
    <div className={styles.consultationSection}>
      <div className={styles.consultationTitle}>
        Book a Customised Financing Consultation for your Brand
      </div>
      <div>
        <MeritoButton
          type="primary-1"
          size="lg"
          isRounded={true}
          icon={<FaArrowRight />}
          btnText="Book a Call"
          isIconToRight={true}
          onClick={() => setShowCalendlyPopup(true)}
        />
      </div>
    </div>
  );
};

const Footer: React.FC<FooterNavProps> = ({ navigator }) => {
  return (
    <div className={styles.footer}>
      <div className={styles.footerTopRow}>
        <div
          className={styles.brandLogo}
          onClick={() => navigator("/")}
          style={{ cursor: "pointer" }}
        >
          <img
            style={{ maxWidth: "60%" }}
            src={MeritoDarkLogo}
            alt="brnadlogo"
            className="img-fluid"
            height={"100%"}
            width={"100%"}
          />
        </div>
        <a
          href="https://clickmerito.com/privacypolicy"
          target="_blank"
          style={{ textDecoration: "none" }}
        >
          <div className={styles.footerPrivacyPolicy}>Privacy Policy</div>
        </a>
      </div>
      <div className={styles.footerPrivacyPolicy}>
        &copy; 2025 Merito. All rights reserved.
      </div>
    </div>
  );
};

const SFPLLandingPage = () => {
  const navigator = useNavigate();
  const [searchParams] = useSearchParams();

  const [showCalendlyPopup, setShowCalendlyPopup] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const initialParams = useMemo(
    () => ({
      brandName: searchParams.get("brandName") || "",
      phoneCode: searchParams.get("phoneCode") || "",
      phoneNumber: searchParams.get("phoneNumber") || "",
    }),
    []
  );

  console.log("initial data", initialParams);

  useEffect(() => {
    navigator("/sfpl", { replace: true });
    const bodyElement = document.getElementsByTagName("body")[0];
    bodyElement.style.overflowY = "auto";
    const pageIndexLayout: any = document.getElementById("page_body");
    if (pageIndexLayout) {
      pageIndexLayout.style.overflowY = "scroll";
    }
  }, [navigator]);

  return (
    <div className={styles.rootContainer}>
      <HeaderNav navigator={navigator} />
      <HeroSection
        setShowCalendlyPopup={setShowCalendlyPopup}
        isLoading={isLoading}
        isSuccess={isSuccess}
        setIsLoading={setIsLoading}
        setIsSuccess={setIsSuccess}
        prefillValues={initialParams}
      />
      <ReasonSection />
      <AdditionalItemsSection />
      <BookConsultationSection setShowCalendlyPopup={setShowCalendlyPopup} />
      <Footer navigator={navigator} />
      <CalendlyPopup
        showPopup={showCalendlyPopup}
        setShowPopup={setShowCalendlyPopup}
        prefillValues={{ name: "", email: "" }}
      />
    </div>
  );
};

export default SFPLLandingPage;
